import { useState } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { FilterType } from "routes/group/components/AttendantDataTable";
import { useNavigate, useParams } from "react-router-dom";
import { useGetIdentity } from "@refinedev/core";
import { useGetDetail } from "routes/group/hooks/useGetDetail";
import { useGetEvents } from "routes/group/hooks/useGetEvents";
import useQueryParams from "routes/group/hooks/useQueryParams";
import { useEdit } from "routes/group/hooks/useEdit";
import { useGetAttendants } from "routes/group/hooks/useGetAttendants";
import { useActionSingleAttendant } from "routes/group/hooks/useActionSingleAttendant";
import { useActionMultiAttendant } from "routes/group/hooks/useActionMultiAttendant";
import { useImportAttendants } from "routes/group/hooks/useImportAttendants";
import { useGetGroupHistory } from "routes/group/hooks/useGetGroupHistory";
import { useUpdateDiscount } from "routes/group/hooks/useUpdateDiscount";
import { isHaveActionPermission } from "utills/helpers";
import {
  ActionMultiAttendantPayload,
  ActionSingleAttendantPayload,
  EditGroupPayload,
  ResponseImportErrorType,
} from "routes/group/types";
import MultiActionErrorModal, {
  MultiActionErrorType,
} from "routes/group/components/MultiActionErrorModal";
import { IUserData } from "interfaces";
import {
  ClockIcon,
  EyeIcon,
  PencilIcon,
} from "@heroicons/react/outline";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import Button from "components/button";
import GroupForm from "routes/group/components/form";
import ValidImportAttendantModal from "routes/group/components/ValidImportAttendantModal";
import InValidImportAttendantModal from "routes/group/components/InValidImportAttendantModal";
import LogHistoryModal from "routes/group/components/LogHistoryModal";
import ImportAttendantErrorModal, {
  ImportAttendantErrorType,
} from "routes/group/components/ImportAttendantErrorModal";

export interface SelectedAttendantType {
  member_id: string;
  registered_product_id: string;
}

export interface InValidTableType {
  key: string;
  value: string;
  row: number;
  message: string;
}

const GroupManagementEditPage = () => {
  const PAGE_SIZE = 10;

  const ALLOWED_TYPES = [
    "text/csv", // CSV
    "application/vnd.ms-excel", // XLS (cũ)
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // XLSX
  ];

  const { id } = useParams();
  const { searchParams, paramsObject, setQueryParams } = useQueryParams();
  const navigate = useNavigate();

  const { data: user } = useGetIdentity<IUserData>();

  // Attendant query params
  const attendantPage = searchParams.get("attendant_page")
    ? Number(searchParams.get("attendant_page"))
    : 1;
  const attendantQuery = searchParams.get("attendant_q") ?? "";
  const attendantGroupId = searchParams.get("attendant_group_id") ?? "";
  const attendantPaymentStatus =
    searchParams.get("attendant_payment_status") ?? "";

  // Get detail group information
  const { data, loading, fetchData } = useGetDetail(id ?? "");

  // Get group attendants
  const {
    data: attendants,
    loading: attendantsLoading,
    paging: attendantPaging,
    params: attendantParams,
    setParams: setAttendantParams,
    fetchData: fetchAttendantData,
  } = useGetAttendants(id ?? "", {
    page: attendantPage,
    q: attendantQuery,
    group_id: attendantGroupId,
    payment_status: attendantPaymentStatus,
    limit: PAGE_SIZE,
  });

  const { data: events, loading: eventsLoading } = useGetEvents();

  const { mutation: informationMutation, loading: informationLoading } =
    useEdit();

  const {
    mutation: actionSingleAttendantMutation,
    loading: actionSingleAttendantLoading,
  } = useActionSingleAttendant();

  const {
    mutation: actionMultiAttendantMutation,
    loading: actionMultiAttendantLoading,
  } = useActionMultiAttendant();

  const { mutation: importAttendantMutation, loading: importAttendantLoading } =
    useImportAttendants();

  const {
    data: logData,
    loading: logLoading,
    paging: logPaging,
    params: logParams,
    setParams: setLogParams,
    fetchData: fetchLogData,
  } = useGetGroupHistory(id ?? "", {
    page: 1,
    limit: 5,
  });

  const { mutation: updateDiscountMutation, loading: updateDiscountLoading } =
    useUpdateDiscount();

  const [attendantFilter, setAttendantFilter] = useState<FilterType>({
    page: attendantPage,
    q: attendantQuery,
    group_id: attendantGroupId,
    payment_status: attendantPaymentStatus,
  });

  const [selectedAttendantIds, setSelectedAttendantIds] = useState<
    SelectedAttendantType[]
  >([]);

  const [importGroupData, setImportGroupData] = useState<
    Record<string, string>[]
  >([]);
  const [loadingImport, setLoadingImport] = useState<boolean>(false);
  const [inValidData, setInValidData] = useState<InValidTableType[]>([]);
  const [openValidImportModal, setOpenValidImportModal] =
    useState<boolean>(false);
  const [openInvalidImportModal, setOpenInvalidImportModal] =
    useState<boolean>(false);
  const [openImportAttendantErrorModal, setOpenImportAttendantErrorModal] =
    useState<boolean>(false);
  const [importAttendantErrorData, setImportAttendantErrorData] = useState<
    ImportAttendantErrorType[]
  >([]);

  const [multiActionError, setMultiActionError] = useState<
    MultiActionErrorType[]
  >([]);
  const [openMultiActionErrorModal, setOpenMultiActionErrorModal] =
    useState<boolean>(false);

  const [openLogHistoryModal, setOpenLogHistoryModal] =
    useState<boolean>(false);

  const [logFilter, setLogFilter] = useState<{ page: number }>({ page: 1 });

  const handleChangeAttendantFilter = (key: string, value: string) => {
    setAttendantFilter({
      ...attendantFilter,
      [key]: value,
    });
  };

  const handleChangeAttendantPage = (page: number) => {
    setAttendantFilter({ ...attendantFilter, page: page });
    // Update URL search params
    setQueryParams({
      attendant_page: page.toString(),
    });
    setAttendantParams({ ...attendantParams, page: page }); // Update API params
  };

  const handleSearchAttendant = async (data: FilterType) => {
    console.log("Search:", data);

    // Update URL params
    setQueryParams({
      attendant_page: "1",
      attendant_q: attendantFilter.q,
      attendant_group_id: attendantFilter.group_id,
      attendant_payment_status: attendantFilter.payment_status,
    });

    // Update API params
    setAttendantParams({
      page: 1,
      q: attendantFilter.q,
      group_id: attendantFilter.group_id,
      payment_status: attendantFilter.payment_status,
      limit: PAGE_SIZE,
    });
  };

  const handleSubmitForm = async (payload: EditGroupPayload) => {
    console.log("Submit form:", payload);

    const res = await informationMutation(payload);

    if (res.success) {
      // Refech detail group API
      fetchData(id ?? "");

      // Refech log history API
      setLogFilter({ ...logFilter, page: 1 });
      fetchLogData(id ?? "", { ...logParams, page: 1 });

      toast.success("Edit group information successfully");
    }
  };

  const handleActionSingleAttendant = async (
    group_id: string,
    payload: ActionSingleAttendantPayload,
  ) => {
    console.log({ group_id, payload });

    const res = await actionSingleAttendantMutation(group_id, payload);

    if (res.success) {
      // Refetch group detail API
      fetchData(id ?? "");

      // Refech attendant API
      fetchAttendantData(id ?? "", attendantParams);

      toast.success("Action single member successfully");
    }
  };

  const handleRefetchGroupAttendant = () => {
    fetchAttendantData(id ?? "", attendantParams);
  };

  // Handle single check/uncheck
  const toggleSelectAttendantId = (item: SelectedAttendantType) => {
    setSelectedAttendantIds((prev) => {
      const exists = prev.some(
        (attendant) => attendant.member_id === item.member_id,
      );

      if (exists) {
        // có rồi → bỏ ra
        return prev.filter(
          (attendant) => attendant.member_id !== item.member_id,
        );
      }

      // chưa có → add vào
      return [...prev, item];
    });
  };

  // Handle check/uncheck all
  const toggleSelectMultiAttendantId = (items: SelectedAttendantType[]) => {
    setSelectedAttendantIds((prev) => {
      const map = new Map(prev.map((item) => [item.member_id, item]));

      const isAllChecked = items.every((item) => map.has(item.member_id));

      if (isAllChecked) {
        // đã chọn hết → bỏ hết
        items.forEach((item) => {
          map.delete(item.member_id);
        });
      } else {
        // chưa chọn hết → chọn hết
        items.forEach((item) => {
          map.set(item.member_id, item);
        });
      }

      return Array.from(map.values());
    });
  };

  const handleClearAllSelectAttendantId = () => {
    setSelectedAttendantIds([]);
  };

  const handleActionMultiAttendant = async (
    group_id: string,
    payload: ActionMultiAttendantPayload,
  ) => {
    console.log({ group_id, payload });

    const res = await actionMultiAttendantMutation(group_id, payload);

    // Full success
    if (res.success && res.error_count === 0) {
      // Refetch group detail API
      fetchData(id ?? "");

      // Refech attendant API
      fetchAttendantData(id ?? "", attendantParams);

      // Clear selected attendants state
      handleClearAllSelectAttendantId();

      toast.success("Action multiple member successfully");
    }

    // Both success & failed
    if (res.success && res.success_count !== 0 && res.error_count !== 0) {
      toast(res.message);

      // Refetch group detail API
      fetchData(id ?? "");

      // Refech attendant API
      fetchAttendantData(id ?? "", attendantParams);

      // Clear selected attendants state
      handleClearAllSelectAttendantId();
    }

    // Have error list
    if (res.error_count !== 0 && res.error) {
      setMultiActionError(res.error);
      setOpenMultiActionErrorModal(true);
    }
  };

  const handleCloseMultiActionErrorModal = () => {
    setMultiActionError([]);
    setOpenMultiActionErrorModal(false);
  };

  // Handle Import Group
  const handleResetImport = () => {
    setOpenValidImportModal(false);
    setOpenInvalidImportModal(false);
    setImportGroupData([]);
    setInValidData([]);
  };

  const handleValidateFields = (rawData: Record<string, string>[]) => {
    const errList: InValidTableType[] = [];

    rawData.forEach((rowData, rowIndex) => {
      Object.entries(rowData).forEach(([key, value]) => {
        // Product ID field validate
        if (key === "product_id") {
          // Check required
          if (!value) {
            errList.push({
              key,
              value,
              row: rowIndex + 2, // +2 vì có header Excel
              message: "Product ID is required",
            });
          }
        }
      });
    });

    return {
      success: errList.length === 0,
      errList,
    };
  };

  const handleChooseFile = async () => {
    const input = document.createElement("input");
    input.type = "file";

    input.onchange = async (e: any) => {
      handleResetImport();

      const file = e.target.files[0];
      const reader = new FileReader();

      if (!file) {
        toast.error("Can not read choosen file");
        return;
      }

      // Kiểm tra loại file
      if (file && !ALLOWED_TYPES.includes(file.type)) {
        toast.error("File format valid: .csv, .xlsx");
        return;
      }

      setLoadingImport(true);

      reader.onload = async (evt: any) => {
        try {
          const arrayBuffer = evt.target.result as ArrayBuffer;

          // Đọc file bằng arrayBuffer thay vì binary string
          const wb = XLSX.read(arrayBuffer, { type: "array" });

          const wsname = wb.SheetNames[0];
          const ws = wb.Sheets[wsname];
          const rawData = XLSX.utils.sheet_to_json(ws, {
            raw: false,
            defval: "",
          }) as Record<string, string>[];

          // Format dữ liệu
          const submitData: Record<string, string>[] = [];

          if (rawData && rawData.length > 0) {
            rawData.forEach((item: Record<string, string>) => {
              submitData.push(item);
            });
          }

          // Validate dữ liệu
          const validResult = handleValidateFields(rawData);

          console.log({ validResult, submitData });

          if (!validResult.success) {
            toast.error("Invalid data");
            setOpenInvalidImportModal(true);
            setInValidData(validResult.errList);
          } else {
            setImportGroupData([...submitData]);
            setOpenValidImportModal(true);
          }
        } finally {
          setLoadingImport(false);
        }
      };

      reader.readAsArrayBuffer(file);
    };

    input.click();
  };

  const handleSubmitImportAttendant = async (
    payload: Record<string, string>[],
  ) => {
    // console.log("Import data:", payload);

    if (!data?.id) {
      toast.error("Group ID not found");
      return;
    }

    const res = await importAttendantMutation(data?.id, {
      pwd_group: data?.pwd_group ? data?.pwd_group : "",
      attendants: payload,
    });

    console.log("Import data response:", res);

    if (res.success) {
      toast.success("Import successfully");
      handleCloseValidSubmitAttendantModal();

      // Refetch attendant list
      fetchAttendantData(data?.id, attendantParams);
    }

    if (!res.success) {
      toast.error(res.message);
      handleCloseValidSubmitAttendantModal();

      // Refetch attendant list
      fetchAttendantData(data?.id, attendantParams);
    }

    // Have error list
    if (res.error) {
      const formatError: ImportAttendantErrorType[] = res.error?.map(
        (err: ResponseImportErrorType) => {
          return {
            row: err?.row,
            product_id: err?.product_id,
            column: err?.column,
            column_key: err?.column_key,
            invalid_value: err?.invalid_value,
            message: err?.message,
            valid_options: err?.valid_options,
          };
        },
      );

      setOpenImportAttendantErrorModal(true);
      setImportAttendantErrorData(formatError);
    }
  };

  const handleCloseValidSubmitAttendantModal = () => {
    setOpenValidImportModal(false);
    setImportGroupData([]);
  };

  const handleCloseInvalidImportAttendantModal = () => {
    setOpenInvalidImportModal(false);
    setInValidData([]);
  };

  const handleCloseImportAttendantErrorModal = () => {
    setOpenImportAttendantErrorModal(false);
    setImportAttendantErrorData([]);
  };

  // Handle log history
  const handleCloseLogHistoryModal = () => {
    setOpenLogHistoryModal(false);
  };

  const handleLogHistoryPageChange = (value: number) => {
    setLogFilter({ ...logFilter, page: value });

    // Update API search params
    setLogParams({
      ...logParams,
      page: value,
    });
  };

  // Handle update discount
  const handleUpdateDiscount = async () => {
    if (!id) {
      toast.error("Group ID not found");
      return;
    }

    const res = await updateDiscountMutation(id);

    if (res.success) {
      // Refech detail group API
      fetchData(id);

      // Refech log history API
      setLogFilter({ ...logFilter, page: 1 });
      fetchLogData(id, { ...logParams, page: 1 });

      toast.success("Update group discount successfully");
    }
  };

  const handleRefetchDetailGroup = () => {
    // Refetch group detail API
    fetchData(id ?? "");
  };

  return (
    <>
      {/* Multi Action Error Modal */}
      <MultiActionErrorModal
        open={openMultiActionErrorModal}
        data={multiActionError}
        handleClose={handleCloseMultiActionErrorModal}
      />

      {/* Valid Attendant Import Modal */}
      <ValidImportAttendantModal
        data={importGroupData}
        open={openValidImportModal}
        loading={importAttendantLoading}
        handleClose={handleCloseValidSubmitAttendantModal}
        handleSubmitImportAttendant={handleSubmitImportAttendant}
      />

      {/* InValid Attendant Import Modal */}
      <InValidImportAttendantModal
        data={inValidData}
        open={openInvalidImportModal}
        handleClose={handleCloseInvalidImportAttendantModal}
      />

      {/* Import Attendant Error Data Modal */}
      <ImportAttendantErrorModal
        data={importAttendantErrorData}
        open={openImportAttendantErrorModal}
        handleClose={handleCloseImportAttendantErrorModal}
      />

      {/* Log History Modal */}
      {data && (
        <LogHistoryModal
          open={openLogHistoryModal}
          targetGroup={data}
          handleClose={handleCloseLogHistoryModal}
          handlePageChange={handleLogHistoryPageChange}
          data={logData}
          loading={logLoading}
          paging={logPaging}
        />
      )}

      <section className="wrapper">
        {(loading ||
          attendantsLoading ||
          eventsLoading ||
          actionSingleAttendantLoading ||
          actionMultiAttendantLoading) && <FullScreenLoading />}

        <Breadcrumbs
          customPageLabel="Edit Group"
          additionalLabel={
            <div className="flex flex-wrap gap-x-3">
              {isHaveActionPermission("", "group_buy_management", "edit") && (
                <Button
                  className="w-11 h-11 sm:w-auto"
                  loading={updateDiscountLoading}
                  onClick={() => {
                    handleUpdateDiscount();
                  }}
                >
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">
                    Update Discount
                  </span>
                </Button>
              )}

              {isHaveActionPermission("", "group_buy_management", "edit") && (
                <Button
                  className="w-11 h-11 sm:w-auto"
                  onClick={() => {
                    // navigate(
                    //   `/group-management/detail/${id ?? ""}?event_id=${get(
                    //     data,
                    //     "event_id",
                    //     ""
                    //   )}`
                    // );

                    navigate(`/group-management/detail/${id ?? ""}`);
                  }}
                >
                  <EyeIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Detail</span>
                </Button>
              )}

              <Button
                type="button"
                className="w-11 h-11 bg-white rounded-md px-3 text-brand-500 py-2
                            border-solid border-brand-500 border-[1px]
                            hover:bg-white active:bg-white active:opacity-85 sm:w-auto"
                onClick={() => {
                  setOpenLogHistoryModal(true);
                }}
              >
                <ClockIcon className="size-4 text-brand-500" />{" "}
                <span className="hidden sm:block sm:ml-2">Log</span>
              </Button>

              {/* {isHaveActionPermission("", "group_buy_management", "create") && (
                <Button
                  className="w-11 h-11 sm:w-auto"
                  loading={loadingImport}
                  onClick={() => {
                    handleChooseFile();
                  }}
                >
                  <UploadIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Import Attendant</span>
                </Button>
              )} */}
            </div>
          }
        />

        {!loading && !attendantsLoading && !eventsLoading && !data && (
          <div className="w-full min-h-[calc(100%-72px)] bg-[#f6f5fe] flex flex-col items-center py-[50px]">
            <div className="text-brand-500 text-center">
              <div className="text-36 font-700 mb-[16px]">Group not found</div>
              <div className="text-20 mb-[8px]">Group does not exist</div>
              <div className="text-20">
                Please contact your Administrator for assistance
              </div>
            </div>
          </div>
        )}

        {!loading && !attendantsLoading && !eventsLoading && data && (
          <GroupForm
            user={user ?? null}
            data={data}
            events={events}
            loading={informationLoading}
            attendants={attendants}
            attendantPaging={attendantPaging}
            handleSubmitForm={handleSubmitForm}
            handleChangeAttendantPage={handleChangeAttendantPage}
            attendantFilter={attendantFilter}
            handleChangeAttendantFilter={handleChangeAttendantFilter}
            handleSearchAttendant={handleSearchAttendant}
            handleActionSingleAttendant={handleActionSingleAttendant}
            selectedAttendantIds={selectedAttendantIds}
            toggleSelectAttendantId={toggleSelectAttendantId}
            toggleSelectMultiAttendantId={toggleSelectMultiAttendantId}
            handleClearAllSelectAttendantId={handleClearAllSelectAttendantId}
            handleActionMultiAttendant={handleActionMultiAttendant}
            handleRefetchGroupAttendant={handleRefetchGroupAttendant}
            loadingImport={loadingImport}
            handleChooseFile={handleChooseFile}
            handleRefetchDetailGroup={handleRefetchDetailGroup}
          />
        )}
      </section>
    </>
  );
};

export default GroupManagementEditPage;
